import { useState, useEffect } from "react";
import { Brain, Key, TestTube, CheckCircle, XCircle, Loader2, Eye, EyeOff, Zap, Trash2, ShieldCheck, Sparkles, Cpu } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_INFO = {
  openai: {
    label: "OpenAI Direct",
    description: "Connect directly to OpenAI with your own API key. Supports GPT-4o and all OpenAI models.",
    placeholder: "sk-proj-...",
    color: "text-emerald-600",
    bg: "bg-emerald-50 border-emerald-200",
    models: [
      { id: "gpt-4o", label: "GPT-4o (Recommended - Multimodal & Fast)" },
      { id: "gpt-4o-mini", label: "GPT-4o Mini (Cost Effective)" },
      { id: "gpt-4-turbo", label: "GPT-4 Turbo (High Precision)" },
      { id: "o3-mini", label: "o3-mini (Reasoning Model)" },
    ],
  },
  joule: {
    label: "SAP Joule",
    description: "SAP's AI copilot powered by SAP AI Core. Requires SAP BTP AI Core credentials.",
    placeholder: "Enter SAP AI Core API key",
    color: "text-blue-600",
    bg: "bg-blue-50 border-blue-200",
    models: [
      { id: "gpt-4o", label: "SAP Joule Default (GPT-4o Core)" },
    ],
  },
};

const AIConfiguration = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("openai");
  const [modelName, setModelName] = useState("gpt-4o");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/ai-config`);
      setConfig(res.data);
      setProvider(res.data.provider === "emergent" ? "openai" : (res.data.provider || "openai"));
      if (res.data.model_name) {
        setModelName(res.data.model_name);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load AI configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleTest = async () => {
    if (!apiKey.trim()) {
      toast.error("Enter an API key to test");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/settings/ai-config/test`, {
        provider,
        api_key: apiKey.trim(),
        model_name: modelName,
      });
      setTestResult(res.data);
      if (res.data.success) {
        toast.success("AI key verified successfully!");
      } else {
        toast.error(res.data.message || "Test failed");
      }
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.message || "Test request failed" });
      toast.error("Test request failed");
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error("Enter an API key to save");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/ai-config`, {
        provider,
        api_key: apiKey.trim(),
        model_name: modelName,
      });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success("AI configuration saved successfully!");
        setConfig(res.data);
        setApiKey("");
        setTestResult(null);
        window.dispatchEvent(new CustomEvent("ai-config-changed", { detail: { has_key: true } }));
      }
    } catch (e) {
      toast.error("Failed to save AI configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKey = async () => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/settings/ai-config`);
      toast.success("Custom API key deleted. Reverted to system environment key.");
      setConfig(res.data);
      setApiKey("");
      setTestResult(null);
      window.dispatchEvent(new CustomEvent("ai-config-changed", { detail: { has_key: Boolean(res.data?.has_key) } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete API key");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const info = PROVIDER_INFO[provider] || PROVIDER_INFO.openai;
  const currentProviderInfo = PROVIDER_INFO[config?.provider] || PROVIDER_INFO.openai;

  return (
    <div className="space-y-6" data-testid="ai-configuration">
      {/* Current Configuration Card */}
      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Zap className="w-5 h-5 text-amber-500" />
                Current AI Configuration
              </CardTitle>
              <CardDescription>Active AI provider powering agent modules across the platform</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {config?.has_key && (
                <Badge variant="outline" className="gap-1 text-emerald-700 bg-emerald-50 border-emerald-200" data-testid="ai-status-badge">
                  <CheckCircle className="w-3 h-3 text-emerald-600" /> Active
                </Badge>
              )}

              {config?.source === "database" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                      disabled={deleting}
                      data-testid="delete-ai-key-btn"
                    >
                      {deleting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                      Delete Key
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                        <Trash2 className="w-5 h-5" /> Delete Custom AI Key
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete your custom API key? The system will revert to the default environment fallback key.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteKey}
                        className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                      >
                        Yes, Delete Key
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div className="p-2.5 rounded-md bg-secondary/50 border border-border/50">
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
                <Brain className="w-3.5 h-3.5 text-primary" /> Provider
              </p>
              <p className="font-semibold text-foreground">{currentProviderInfo?.label || config?.provider}</p>
            </div>
            <div className="p-2.5 rounded-md bg-secondary/50 border border-border/50">
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
                <Cpu className="w-3.5 h-3.5 text-blue-500" /> Active Model
              </p>
              <p className="font-semibold text-foreground">{config?.model_name || "gpt-4o"}</p>
            </div>
            <div className="p-2.5 rounded-md bg-secondary/50 border border-border/50">
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
                <Key className="w-3.5 h-3.5 text-amber-500" /> Key Preview
              </p>
              <p className="font-mono text-xs font-semibold text-foreground">{config?.key_preview || "Not configured"}</p>
            </div>
            <div className="p-2.5 rounded-md bg-secondary/50 border border-border/50">
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-violet-500" /> Key Origin
              </p>
              <Badge variant="secondary" className="text-xs font-medium">
                {config?.source === "database" ? "Custom Settings Key" : "Environment Default"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security & Storage Notice */}
      <div className="flex items-center gap-3 p-3 rounded-md bg-blue-50/70 border border-blue-200/80 text-xs text-blue-900">
        <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0" />
        <div>
          <span className="font-semibold">AES-256 Encrypted Key Storage:</span> Custom API keys are encrypted at rest using AES-GCM prior to database persistence and are decrypted only during active server inference calls.
        </div>
      </div>

      {/* Update Configuration Card */}
      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Key className="w-5 h-5 text-primary" />
            Update AI Provider & Credentials
          </CardTitle>
          <CardDescription>Configure provider, choose model edition, and input your API key.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Provider Selection */}
          <div>
            <Label className="text-sm font-medium mb-3 block">AI Provider</Label>
            <RadioGroup value={provider} onValueChange={(v) => { setProvider(v); setTestResult(null); setModelName(PROVIDER_INFO[v]?.models[0]?.id || "gpt-4o"); }} className="space-y-2">
              {Object.entries(PROVIDER_INFO).map(([key, val]) => (
                <label
                  key={key}
                  data-testid={`provider-${key}`}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${provider === key ? val.bg : "border-border hover:bg-muted/50"}`}
                >
                  <RadioGroupItem value={key} className="mt-0.5" />
                  <div>
                    <p className={`font-semibold text-sm ${provider === key ? val.color : ""}`}>{val.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{val.description}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Model Selection */}
          <div className="space-y-2">
            <Label htmlFor="model-select" className="text-sm font-medium">Select Model Edition</Label>
            <Select value={modelName} onValueChange={setModelName}>
              <SelectTrigger id="model-select" data-testid="select-model-name">
                <SelectValue placeholder="Select Model Edition" />
              </SelectTrigger>
              <SelectContent>
                {info.models.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* API Key Input */}
          <div className="space-y-2">
            <Label htmlFor="ai-key" className="text-sm font-medium">API Key</Label>
            <div className="relative">
              <Input
                id="ai-key"
                data-testid="ai-key-input"
                type={showKey ? "text" : "password"}
                placeholder={info.placeholder}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="pr-10 font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Test Result */}
          {testResult && (
            <div
              data-testid="ai-test-result"
              className={`flex items-start gap-2 p-3 rounded-lg text-sm ${testResult.success ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-800 border border-red-200"}`}
            >
              {testResult.success ? <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
              <div>
                <p className="font-semibold">{testResult.message}</p>
                {testResult.response && (
                  <p className="text-xs mt-1 font-mono bg-white/70 p-1.5 rounded border border-emerald-200/60">Sample Response: &quot;{testResult.response}&quot;</p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={testing || !apiKey.trim()}
              data-testid="ai-test-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
              Test API Key
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !apiKey.trim()}
              data-testid="ai-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
              Save Configuration
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIConfiguration;
